import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutGrid } from 'lucide-react';

import { useTranslation } from '@sshop/i18n';
import type { PageSection } from '@sshop/domain';

import { useCategoryTree } from '@/features/categories';
import { usePageSections } from '@/features/home';

import { CategoriesMegaPanel } from './CategoriesMegaPanel';
import { CategoriesMobileDrawer } from './CategoriesMobileDrawer';
import { categoriesMenuStyles as s } from './CategoriesMenu.styles';

const DESKTOP_MENU_QUERY = '(min-width: 1274px)';

function menuPromos(banners: readonly PageSection[] | undefined): readonly PageSection[] {
  return (banners ?? [])
    .filter((banner) => banner.showInCategoriesMenu)
    .sort((a, b) => a.categoriesMenuOrder - b.categoriesMenuOrder || a.id.localeCompare(b.id))
    .slice(0, 2);
}

/**
 * Desktop-only Categories mega menu trigger + popover panel.
 * Mount once inside the header actions row.
 */
export function CategoriesDesktopMenu() {
  const { t } = useTranslation();
  const { data, isLoading } = useCategoryTree();
  const roots = data ?? [];
  const { data: banners } = usePageSections('home');
  const promos = menuPromos(banners);

  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(64);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const header = rootRef.current?.closest('header');
    if (!header) return;

    const updateTop = () => setPanelTop(header.getBoundingClientRect().bottom);
    updateTop();
    const observer = new ResizeObserver(updateTop);
    observer.observe(header);
    window.addEventListener('resize', updateTop);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateTop);
    };
  }, [open]);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MENU_QUERY);
    const closeWhenLeavingDesktop = () => {
      if (!media.matches) setOpen(false);
    };
    media.addEventListener('change', closeWhenLeavingDesktop);
    return () => media.removeEventListener('change', closeWhenLeavingDesktop);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      const isTrigger = rootRef.current?.contains(target) ?? false;
      const isPanel = target instanceof Element && target.closest('[data-categories-menu-panel]');
      if (!isTrigger && !isPanel) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div ref={rootRef} className={s.desktopWrap}>
        <button
          type="button"
          className={s.trigger}
          data-open={open || undefined}
          aria-label={t('nav.allCategories')}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          disabled={isLoading && roots.length === 0}
        >
          <LayoutGrid className={s.triggerIcon} aria-hidden />
        </button>
      </div>
      {open && roots.length > 0
        ? createPortal(
            <>
              <div
                className={s.panelScrim}
                style={{ top: panelTop }}
                aria-hidden
                onClick={() => setOpen(false)}
              />
              <CategoriesMegaPanel
                roots={roots}
                promos={promos}
                top={panelTop}
                onNavigate={() => setOpen(false)}
              />
            </>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * Mobile-only Categories trigger (icon button) + slide-in drawer (< md).
 * Mount once inside the header actions row.
 */
export function CategoriesMobileMenu() {
  const { t } = useTranslation();
  const { data } = useCategoryTree();
  const roots = data ?? [];
  const { data: banners } = usePageSections('home');
  const promos = menuPromos(banners);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MENU_QUERY);
    const closeWhenEnteringDesktop = () => {
      if (media.matches) setOpen(false);
    };
    media.addEventListener('change', closeWhenEnteringDesktop);
    return () => media.removeEventListener('change', closeWhenEnteringDesktop);
  }, []);

  return (
    <>
      <button
        type="button"
        className={s.mobileTrigger}
        aria-label={t('nav.categories')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <LayoutGrid className={s.mobileTriggerIcon} aria-hidden />
      </button>
      <CategoriesMobileDrawer
        roots={roots}
        promos={promos}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
